// BrimLight/client/helpers/video-editor/TrackSettingsPanel.js

import { ComponentBase } from "../../core/ComponentBase.js";

export class TrackSettingsPanel extends ComponentBase {
    static styles = `
        .track-settings-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1040;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
        }
        .track-settings-backdrop.show {
            opacity: 1;
            pointer-events: auto;
        }
        .track-settings-panel {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: 320px;
            max-width: 90vw;
            background-color: var(--bs-body-bg);
            border-left: 1px solid var(--bs-border-color);
            box-shadow: -5px 0 15px rgba(0,0,0,0.1);
            z-index: 1041;
            transform: translateX(100%);
            transition: transform 0.3s ease-in-out;
            display: flex;
            flex-direction: column;
        }
        .track-settings-panel.show {
            transform: translateX(0);
        }
        .panel-header {
            flex-shrink: 0;
            padding: 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .panel-header h5 {
            margin: 0;
            font-size: 1.1rem;
        }
        .panel-body {
            flex-grow: 1;
            padding: 1.5rem 1rem;
            overflow-y: auto;
        }
        .track-setting-row {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.25rem;
        }
        .track-setting-row label {
            flex-basis: 90px;
            font-size: 0.9rem;
            font-weight: 500;
        }
        .track-setting-row input[type="range"] {
            flex-grow: 1;
        }
    `;

    constructor(config) {
        super(config);
        this.trackSettings = {};
        this.boundHandleValueChange = this.handleValueChange.bind(this);
        
        // --- NEW: Debounce function for volume changes ---
        // This prevents sending a save request for every pixel the slider moves.
        this.debouncedEmit = this.debounce((...args) => this.emit(...args), 400);

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.innerHTML = `
            <div class="track-settings-backdrop"></div>
            <div class="track-settings-panel">
                <div class="panel-header">
                    <h5><i class="bi bi-sliders me-2"></i>Track Mixer</h5>
                    <button type="button" class="btn-close" aria-label="Close"></button>
                </div>
                <div class="panel-body"></div>
            </div>
        `;
        this.backdrop = this.container.querySelector('.track-settings-backdrop');
        this.panel = this.container.querySelector('.track-settings-panel');
        this.panelBody = this.container.querySelector('.panel-body');
        this.closeButton = this.container.querySelector('.btn-close');

        this.backdrop.addEventListener('click', () => this.hide());
        this.closeButton.addEventListener('click', () => this.hide());

        await super.initialize();
    }

    setData(currentSettings = {}) {
        const audioTracks = ['dialogue', 'sfx', 'bgMusic', 'ambient'];
        const defaultTrackSettings = {
            dialogue: { volume: 1.0, muted: false },
            sfx: { volume: 0.8, muted: false },
            bgMusic: { volume: 0.3, muted: false },
            ambient: { volume: 0.5, muted: false }
        };

        this.trackSettings = {};
        audioTracks.forEach(track => {
            this.trackSettings[track] = { ...defaultTrackSettings[track], ...currentSettings[track] };
        });

        this.panelBody.innerHTML = ''; // Clear previous content
        audioTracks.forEach(trackType => {
            const settings = this.trackSettings[trackType];
            const row = document.createElement('div');
            row.className = 'track-setting-row';
            row.innerHTML = `
                <label for="vol-${trackType}">${this.t(trackType)}</label>
                <button class="btn btn-sm btn-outline-secondary" data-track="${trackType}" data-action="mute" title="Mute/Unmute Track">
                    <i class="bi ${settings.muted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}"></i>
                </button>
                <!-- START OF FIX: Adjust range to the valid [0, 1] for HTML Audio -->
                <input type="range" class="form-range" id="vol-${trackType}" min="0" max="1" step="0.01" value="${settings.volume}" data-track="${trackType}" data-action="volume">
                <!-- END OF FIX -->
            `;
            this.panelBody.appendChild(row);
        });

        this.panelBody.removeEventListener('input', this.boundHandleValueChange);
        this.panelBody.removeEventListener('click', this.boundHandleValueChange);
        this.panelBody.addEventListener('input', this.boundHandleValueChange);
        this.panelBody.addEventListener('click', this.boundHandleValueChange);
    }


    handleValueChange(e) {
        const target = e.target.closest('[data-action]');
        if (!target) return;

        const action = target.dataset.action;
        const track = target.dataset.track;

        // --- START OF FIX: Handle events specifically to prevent double-firing ---
        if (action === 'volume' && e.type === 'input') {
            this.trackSettings[track].volume = parseFloat(target.value);
            // Emit an immediate event for real-time preview
            this.emit('trackSettingsChanged', { trackSettings: this.trackSettings });
            // Emit a debounced event for saving
            this.debouncedEmit('trackSettingsChangeDebounced', { trackSettings: this.trackSettings });
        } else if (action === 'mute' && e.type === 'click') {
            this.trackSettings[track].muted = !this.trackSettings[track].muted;
            const icon = target.querySelector('i');
            icon.className = `bi ${this.trackSettings[track].muted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`;
            // Mute is a single action, so it can be saved immediately.
            this.emit('trackSettingsChanged', { trackSettings: this.trackSettings, save: true });
        }
        // --- END OF FIX ---
    }

    debounce(func, delay) {
        let timeout;
        return function(...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), delay);
        };
    }

    show() {
        this.backdrop.classList.add('show');
        this.panel.classList.add('show');
    }

    hide() {
        this.backdrop.classList.remove('show');
        this.panel.classList.remove('show');
    }

    render() {
        return this.container;
    }
}